import { Factory, Model, type Collection } from 'miragejs'
import type { ModelDefinition, ModelInstance } from 'miragejs/-types'

import type { ClientData, PhoneData } from '@/models/client.js'
import type { MirageServer, MockServer } from './server.js'
import type { ClientDataWithRelations } from './Client.js'
import type { PatientDataWithRelations } from './Patient.js'
import type {
  BundleNavigationSearchData,
  ItemNavigationSearchData,
  NavigationSearchClientDataItem,
  NavigationSearchResult,
} from '@/composables/api/NavigationSearch.js'
import {
  rand,
  randBetweenDate,
  randChanceBoolean,
  randRecentDate,
} from '@ngneat/falso'
import { toNumber } from '@/utils/number.js'
import type { PatientData } from '@/composables/patients.js'

export const model = Model as ModelDefinition<NavigationSearchResult>

export const factory = Factory.extend<NavigationSearchResult>({
  clients_with_patients() {
    return {
      data: [],
      count: 0,
    }
  },
  items() {
    return {
      data: [],
      count: 0,
    }
  },
  bundles() {
    return {
      data: [],
      count: 0,
    }
  },
})

export function routes(server: MockServer) {
  server.get(
    '/search/search_v3',
    function (this: MirageServer, schema, request) {
      // Define search fields for each entity type
      const CLIENT_SEARCH_FIELDS = ['first_name', 'last_name', 'email'] as const
      const PATIENT_SEARCH_FIELDS = ['name', 'official_name'] as const
      const ITEM_SEARCH_FIELDS = ['name'] as const
      const BUNDLE_SEARCH_FIELDS = ['name'] as const

      // Helper function to check if entity matches search term in specified fields
      const entityMatches = <T>({
        entity,
        fields,
        searchTerm,
        requireAllWords = false,
      }: {
        entity: T
        fields: readonly (keyof T)[]
        searchTerm: string
        requireAllWords?: boolean
      }) => {
        const searchWords = searchTerm
          .toLowerCase()
          .split(/\s+/)
          .filter((word) => word.length > 0)
        if (requireAllWords) {
          return searchWords.every((word) =>
            fields.some((field) => {
              const fieldValue = String(entity[field] || '').toLowerCase()
              return fieldValue.includes(word)
            }),
          )
        }

        return searchWords.some((word) =>
          fields.some((field) => {
            const fieldValue = String(entity[field] || '').toLowerCase()
            return fieldValue.includes(word)
          }),
        )
      }

      // Helper function to generate highlight for any entity
      const generateHighlight = <TEntity>({
        entity,
        fields,
        searchWords,
        fieldMapping = {},
        keyPrefix = '',
      }: {
        entity: TEntity
        fields: readonly (keyof TEntity)[]
        searchWords: string[]
        fieldMapping?: Record<string, string>
        keyPrefix?: string
      }) => {
        const highlight: Record<string, string[]> = {}

        fields.forEach((field) => {
          const fieldValueLower = String(entity[field] || '').toLowerCase()
          const hasMatch = searchWords.some((word) =>
            fieldValueLower.includes(word),
          )
          if (hasMatch) {
            const fieldValue = String(entity[field] || '')
            const fieldKey = String(field)
            const highlightKey = fieldMapping[fieldKey] || fieldKey
            const finalKey = keyPrefix
              ? `${keyPrefix}${highlightKey}`
              : highlightKey
            highlight[finalKey] = [`<em>${fieldValue}</em>`]
          }
        })

        return highlight
      }

      const searchTermParam = request.queryParams['filter[search]']
      const searchTerm = Array.isArray(searchTermParam)
        ? searchTermParam[0] || ''
        : searchTermParam || ''
      const searchWords = searchTerm
        .toLowerCase()
        .split(/\s+/)
        .filter((word) => word.length > 0)

      const resultTypesParam = request.queryParams[
        'filter[result_type.in]'
      ] as string
      const resultTypes = resultTypesParam?.split(',') || []

      // Determine what items should be included based on the Result Types filter
      const includeClientsWithPatients =
        !resultTypes ||
        resultTypes.includes('CLIENT_PATIENT') ||
        (resultTypes.includes('CLIENT') && resultTypes.includes('PATIENT'))
      const includeItems = !resultTypes || resultTypes.includes('ITEM')
      const includeBundles = !resultTypes || resultTypes.includes('BUNDLE')

      const finalResults: {
        clientsAndPatients: ModelInstance<ClientData>[]
        items: ModelInstance<ItemNavigationSearchData>[]
        bundles: ModelInstance<BundleNavigationSearchData>[]
      } = {
        clientsAndPatients: [],
        items: [],
        bundles: [],
      }

      // -- HANDLE CLIENTS AND PATIENTS --
      let clientDirectMatches: ModelInstance<ClientData>[] = []
      let patientDirectMatches: ModelInstance<PatientData>[] = []
      const combinedClientAndPatientMatches: {
        clientData: ModelInstance<ClientData>
        patientData: ModelInstance<PatientData>[]
      }[] = []

      if (includeClientsWithPatients) {
        // Scenario: Search for a single word
        if (searchWords?.length === 1) {
          clientDirectMatches = (
            schema.where('client', (client) =>
              entityMatches({
                entity: client,
                fields: CLIENT_SEARCH_FIELDS,
                searchTerm,
              }),
            ) as Collection<ModelInstance<ClientData>>
          ).models

          patientDirectMatches = (
            schema.where('patient', (patient) =>
              entityMatches({
                entity: patient,
                fields: PATIENT_SEARCH_FIELDS,
                searchTerm,
              }),
            ) as Collection<ModelInstance<PatientData>>
          ).models

          if (patientDirectMatches?.length) {
            patientDirectMatches.forEach((patient) => {
              const patientData =
                patient as ModelInstance<PatientDataWithRelations>
              const clientId = patientData.client.id

              // If Client Data is already queried, then skip
              if (
                clientDirectMatches.some((client) => client.id === clientId)
              ) {
                return
              }

              // If CombinedMatch entry for that client already exists, then add the patient to the existing entry
              const existingCombinedMatch =
                combinedClientAndPatientMatches.find(
                  (match) => match.clientData.id === clientId,
                )
              if (existingCombinedMatch) {
                existingCombinedMatch.patientData.push(patientData)
                return
              }

              // If CombinedMatch entry for that client does not exist, then create a new entry
              const clientData = schema.find('client', clientId!)
              if (clientData) {
                combinedClientAndPatientMatches.push({
                  clientData,
                  patientData: [patientData],
                })
              }
            })
          }
        }

        if (searchWords?.length > 1) {
          // Scenario: Search for multiple words
          clientDirectMatches = (
            schema.where('client', (client) =>
              entityMatches({
                entity: client,
                fields: CLIENT_SEARCH_FIELDS,
                searchTerm,
              }),
            ) as Collection<ModelInstance<ClientData>>
          ).models

          patientDirectMatches = (
            schema.where('patient', (patient) =>
              entityMatches({
                entity: patient,
                fields: PATIENT_SEARCH_FIELDS,
                searchTerm,
              }),
            ) as Collection<ModelInstance<PatientData>>
          ).models

          // Go through each Client and verify if there is any Non-Matching word. If so, then remove the client from the clientDirectMatches array.
          clientDirectMatches.forEach((client) => {
            const clientData = client as ModelInstance<ClientDataWithRelations>
            const clientFullName =
              `${clientData.first_name} ${clientData.last_name}`.toLowerCase()
            const clientEmails = clientData.email
              ? [clientData.email.toLowerCase()]
              : []
            const clientFields = [clientFullName, clientEmails]
            const nonMatchingWords = searchWords.filter((word) =>
              clientFields.every((field) => !field.includes(word)),
            )

            if (nonMatchingWords.length > 0) {
              const hasPatientMatches = patientDirectMatches.some((patient) => {
                const patientData =
                  patient as ModelInstance<PatientDataWithRelations>
                const patientClientId = patientData.client.id
                if (patientClientId === client.id) {
                  return true
                }

                return false
              })

              if (!hasPatientMatches) {
                clientDirectMatches = clientDirectMatches.filter(
                  (c) => c.id !== client.id,
                )
              }
            }
          })

          patientDirectMatches.forEach((patient) => {
            const patientData =
              patient as ModelInstance<PatientDataWithRelations>
            const clientId = patientData.client.id

            const nonMatchingWords = searchWords.filter((word) =>
              entityMatches({
                entity: patient,
                fields: PATIENT_SEARCH_FIELDS,
                searchTerm: word,
              }),
            )

            // If Client Data is already queried, verify if the client matches at least one of the search words
            let patientClientData: ModelInstance<ClientData> | null = null
            if (clientDirectMatches.some((client) => client.id === clientId)) {
              patientClientData = schema.where(
                'client',
                (client) => client.id === clientId,
              ).models[0]
            } else {
              patientClientData = schema.find('client', clientId!)
            }
            if (!patientClientData) {
              return
            }

            if (nonMatchingWords.length > 0) {
              const clientFullName =
                `${patientClientData.first_name} ${patientClientData.last_name}`.toLowerCase()
              const clientEmails = patientClientData.email
                ? [patientClientData.email.toLowerCase()]
                : []
              const clientFields = [clientFullName, clientEmails]
              const clientHasAnyMatchingWords = clientFields.some((field) =>
                searchWords.some((word) => field.includes(word)),
              )
              if (!clientHasAnyMatchingWords) {
                patientDirectMatches = patientDirectMatches.filter(
                  (p) => p.id !== patient.id,
                )
                return
              }
            }

            // If CombinedMatch is not queried, then let's query it
            if (patientClientData) {
              combinedClientAndPatientMatches.push({
                clientData: patientClientData,
                patientData: [patientData],
              })
            }

            // If CombinedMatch entry for that client already exists, then add the patient to the existing entry
            const existingCombinedMatch = combinedClientAndPatientMatches.find(
              (match) => match.clientData.id === clientId,
            )
            if (existingCombinedMatch) {
              existingCombinedMatch.patientData.push(patientData)
              return
            }

            // If CombinedMatch entry for that client does not exist, then create a new entry
            const clientData = schema.find('client', clientId!)
            if (clientData) {
              combinedClientAndPatientMatches.push({
                clientData,
                patientData: [patientData],
              })
            }
          })
        }

        finalResults.clientsAndPatients = clientDirectMatches.concat(
          combinedClientAndPatientMatches.map((match) => match.clientData),
        )
      }

      // Transform clients with patients
      let transformedClientsWithPatients: NavigationSearchClientDataItem[] = []
      if (includeClientsWithPatients) {
        const searchWords = searchTerm
          .toLowerCase()
          .split(/\s+/)
          .filter((word) => word.length > 0)

        transformedClientsWithPatients = finalResults.clientsAndPatients.map(
          (client) => {
            const clientData = client as ModelInstance<ClientDataWithRelations>

            const clientPatients = clientData.patients.models.filter(
              (patient) => {
                const patientData =
                  patient as ModelInstance<PatientDataWithRelations>
                return patientData.client.id === client.id
              },
            )

            // Sort matching patients by relevance and name
            const clientFullName =
              `${clientData.first_name} ${clientData.last_name}`.toLowerCase()
            const clientEmails = clientData.email
              ? [clientData.email.toLowerCase()]
              : []
            const clientFields = [clientFullName, clientEmails]
            const clientHasAnyMatchingWords = clientFields.some((field) =>
              searchWords.some((word) => field.includes(word)),
            )

            const sortedMatchingClientPatients = clientPatients.filter(
              (patient) =>
                searchWords.some((word) =>
                  entityMatches({
                    entity: patient,
                    fields: PATIENT_SEARCH_FIELDS,
                    searchTerm: word,
                  }),
                ),
            )

            const nonMatchingClientPatients = clientHasAnyMatchingWords
              ? clientPatients.filter(
                  (patient) =>
                    !searchWords.some((word) =>
                      entityMatches({
                        entity: patient,
                        fields: PATIENT_SEARCH_FIELDS,
                        searchTerm: word,
                      }),
                    ),
                )
              : []

            // Sort matching patients first (sorted by relevance), then alphabetically sorted non-matching
            const sortedClientPatients = [
              ...sortedMatchingClientPatients.sort((a, b) => {
                const aMatches = searchWords.some((word) =>
                  a.name.toLowerCase().includes(word),
                )
                const bMatches = searchWords.some((word) =>
                  b.name.toLowerCase().includes(word),
                )
                if (aMatches && !bMatches) {
                  return -1
                }
                if (!aMatches && bMatches) {
                  return 1
                }
                return (a.name || '').localeCompare(b.name || '')
              }),
              ...nonMatchingClientPatients.sort((a, b) =>
                (a.name || '').localeCompare(b.name || ''),
              ),
            ]

            // Filter out duplicates by patient.id (keeping the first occurrence)
            const seenIds = new Set<number>()
            const uniqueSortedClientPatients = sortedClientPatients.filter(
              (patient) => {
                const id = toNumber(patient.id)
                if (seenIds.has(id)) {
                  return false
                }

                seenIds.add(id)
                return true
              },
            )

            // Generate highlight based on which fields matched
            const generateClientHighlight = () => {
              // Generate client field highlights
              const clientHighlight = generateHighlight({
                entity: clientData,
                fields: CLIENT_SEARCH_FIELDS,
                searchWords,
                fieldMapping: { email: 'emails' }, // Map email to emails
              })

              // Generate patient field highlights and merge them
              const patientHighlight: Record<string, string[]> = {}
              uniqueSortedClientPatients.forEach((patient) => {
                const patientFields = generateHighlight({
                  entity: patient,
                  fields: PATIENT_SEARCH_FIELDS,
                  searchWords,
                  keyPrefix: 'patients.',
                })

                // Merge patient highlights, collecting multiple values per field
                Object.entries(patientFields).forEach(([key, values]) => {
                  if (!patientHighlight[key]) {
                    patientHighlight[key] = []
                  }
                  patientHighlight[key].push(...values)
                })
              })

              return { ...clientHighlight, ...patientHighlight }
            }

            return {
              id: toNumber(clientData.id),
              id_number: clientData.id_number || '',
              first_name: clientData.first_name || '',
              last_name: clientData.last_name || '',
              address:
                `${clientData.street_address || ''} ${clientData.city || ''}`.trim(),
              emails: clientData.email ? [clientData.email] : [],
              phone_numbers:
                clientData.phoneNumbers?.models?.map(
                  (phone: ModelInstance<PhoneData>) => phone.phone_number,
                ) || [],
              archived: client.archived,
              patients: uniqueSortedClientPatients.map((patient) => {
                return {
                  id: toNumber(patient.id),
                  name: patient.name,
                  official_name: patient.official_name || '',
                  species: patient.species,
                  breed: patient.breed,
                  birth_date: patient.date_of_birth,
                  weight: patient.weight,
                  gender: patient.gender,
                  archived: patient.archived,
                  microchip: patient.microchip,
                  // ## Fields that are different from the Patient's schema:
                  // `gender_string` is the translated value. Since it's not possible to use i18n in the Mock Server,
                  gender_string: `Translated ${patient.gender}`,
                  // `deceased_date` is not present in the Patient's schema, and is specific to the Search Result Response.
                  deceased_date:
                    patient.date_of_birth &&
                    randChanceBoolean({ chanceTrue: 0.05 })
                      ? randBetweenDate({
                          from: patient.date_of_birth,
                          to: randRecentDate(),
                        }).toISOString()
                      : rand([null, undefined]),
                  // `passport_number` is not present in the Patient's schema, and is specific to the Search Result Response.
                  passport_number: null,
                  // `stable_name` is not present in the Patient's schema, and is specific to the Search Result Response.
                  stable_name: null,
                  // `zip_code` is not present in the Patient's schema, and is specific to the Search Result Response.
                  zip_code: null,
                }
              }),
              highlight: generateClientHighlight(),
            }
          },
        )
      }

      // -- HANDLE ITEMS --
      if (includeItems) {
        // Search in items using schema.where for better performance
        finalResults.items = (
          schema.where('organizationItem', (item) =>
            entityMatches({
              entity: item,
              fields: ITEM_SEARCH_FIELDS,
              searchTerm,
              requireAllWords: true,
            }),
          ) as Collection<ModelInstance<ItemNavigationSearchData>>
        ).models
      }

      // Transform results to match API format
      const transformedItems = finalResults.items.map((item) => {
        const itemHighlight = generateHighlight({
          entity: item,
          fields: ITEM_SEARCH_FIELDS,
          searchWords,
        })

        return {
          id: item.id,
          name: item.name,
          barcode: item.barcode,
          price: item.price,
          code: item.code,
          print_name: item.print_name,
          type_code: item.type_code,
          archived: item.archived,
          hide_on_consultation: item.hide_on_consultation,
          hide_on_consultation_search: item.hide_on_consultation_search,
          hide_on_countersale_search: item.hide_on_countersale_search,
          item_list_id: item?.item_list_id,
          vat_group_id: item?.vat_group_id,
          invoice_group_id: item?.invoice_group_id,
          active_substance: item.active_substance,
          species: item.species,
          tags: item.tags,
          item_categories: item.item_categories,
          linked_items: item.linked_items,
          main_item_id: item.main_item_id,
          is_main_item: item.is_main_item,
          is_sub_item: item.is_sub_item,
          highlight: itemHighlight,
        }
      })

      // -- HANDLE BUNDLES --
      if (includeBundles) {
        // Search in bundles using schema.where for better performance
        finalResults.bundles = (
          schema.where('bundle', (bundle) =>
            entityMatches({
              entity: bundle,
              fields: BUNDLE_SEARCH_FIELDS,
              searchTerm,
              requireAllWords: true,
            }),
          ) as Collection<ModelInstance<BundleNavigationSearchData>>
        ).models
      }

      const transformedBundles = finalResults.bundles.map((bundle) => {
        const bundleHighlight = generateHighlight({
          entity: bundle,
          fields: BUNDLE_SEARCH_FIELDS,
          searchWords,
        })

        return {
          id: bundle.id,
          name: bundle.name,
          department_id: bundle.department_id,
          item_list_id: bundle.item_list_id,
          price: bundle.price,
          price_fixed: bundle.price_fixed,
          price_starting_from: bundle.price_starting_from,
          highlight: bundleHighlight,
        }
      })

      // Create the aggregated result
      const result: NavigationSearchResult = {
        clients_with_patients: {
          data: [],
          count: 0,
        },
        items: {
          data: [],
          count: 0,
        },
        bundles: {
          data: [],
          count: 0,
        },
      }

      if (includeClientsWithPatients) {
        Object.assign(result, {
          clients_with_patients: {
            data: transformedClientsWithPatients,
            count: transformedClientsWithPatients.length,
          },
        })
      }

      if (includeItems) {
        Object.assign(result, {
          items: {
            data: transformedItems,
            count: transformedItems.length,
          },
        })
      }

      if (includeBundles) {
        Object.assign(result, {
          bundles: {
            data: transformedBundles,
            count: transformedBundles.length,
          },
        })
      }

      return {
        data: {
          id: null,
          type: 'NavigationSearchResult',
          attributes: result,
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  // Generate Clients and Patients
  void server.lazySeedingManager?.seedBasic('Client')
  void server.lazySeedingManager?.seedBasic('Patient')

  // Generate a specific Client + Patient
  const client = server.create(
    'client',
    {
      first_name: 'John',
      last_name: 'Doe',
      email: 'john.doe@example.com',
    },
    'withPatients',
    'withPhoneNumbers',
  )
  const patient = server.create('patient', {
    name: 'Bobby',
    official_name: 'Bobby',
  })
  patient.update({ client })
  const clientPatients = [...(client.patients.models || []), patient]
  client.update({ patients: clientPatients })

  // Generate Items
  server.create('organizationItem', {
    name: 'Voltaren Item',
    type_code: '2',
  })
  server.createList('organizationItem', 5, {
    vaccination: false,
    controlled_substance: false,
  })
  server.createList('organizationItem', 5, { vaccination: true })
  server.createList('organizationItem', 5, { controlled_substance: true })

  // Generate Bundles
  server.createList('bundle', 15)
  server.create('bundle', {
    name: 'Generic Bundle',
  })
}
